// @flow
import times from 'lodash/fp/times';

import type { TabularOrder, TabularOrderSpecification } from './tabular-order';
import type { TabularFile, XlsFile } from './tabular-file';
import { fromTabularFile as tabularOrderFromTabularFile } from './tabular-order';
import { parsed } from './tabular-file';
import type { Format } from './date';

export const SHIPMENT_COUNT: number = 6;
export const SHIPMENT_COUNT_V2: 12 = 12;
export const FIRST_SHIPMENT_COLUMN: number = 1;
export const FIRST_SHIPMENT_COLUMN_V2: number = 5;
export const FIRST_ALLOCATION_ROW: number = 19;
export const FIRST_ALLOCATION_ROW_V2: number = 20;
export const DATE_FORMAT: Format = 'M/d/y';

export const tabularFileSpecification: XlsFile = {
  type: 'Xls',
  specification: {
    sheet: 'Import',
  },
};

export const tabularOrderSpecification: TabularOrderSpecification = {
  dropEmptyShipments: true,
  shipments: times<number>((idx) => idx + 1)(SHIPMENT_COUNT).map((idx) => ({
    name: {
      row: 0,
      column: idx,
    },
    locationNumber: {
      row: 2,
      column: idx,
    },
    poNumber: {
      row: 3,
      column: idx,
    },
    arriveOnDate: {
      format: DATE_FORMAT,
      cell: {
        row: 4,
        column: idx,
      },
    },
    cancelOnDate: {
      format: DATE_FORMAT,
      cell: {
        row: 6,
        column: idx,
      },
    },
    allocations: {
      stockItemKeyColumn: 0,
      quantityColumn: idx,
      firstRow: FIRST_ALLOCATION_ROW,
      dropZeroes: true,
    },
  })),
  versions: [
    {
      test: {
        cell: {
          row: 0,
          column: 0,
        },
        value: /2\.(0|1)\.0/,
      },
      customerNumber: {
        row: 2,
        column: FIRST_SHIPMENT_COLUMN_V2,
      },
      catalogKey: {
        row: 1,
        column: 0,
      },
      shipments: times<number>((idx) => idx + FIRST_SHIPMENT_COLUMN_V2)(
        SHIPMENT_COUNT_V2,
      ).map((idx) => ({
        name: {
          row: 1,
          column: idx,
        },
        locationNumber: {
          row: 3,
          column: idx,
        },
        poNumber: {
          row: 4,
          column: idx,
        },
        arriveOnDate: {
          format: DATE_FORMAT,
          cell: {
            row: 5,
            column: idx,
          },
        },
        cancelOnDate: {
          format: DATE_FORMAT,
          cell: {
            row: 7,
            column: idx,
          },
        },
        allocations: {
          stockItemKeyColumn: 0,
          quantityColumn: idx,
          firstRow: FIRST_ALLOCATION_ROW_V2,
          dropZeroes: true,
        },
      })),
    },
  ],
};

export const nameParts = (name: string): string[] =>
  name.split('.')[0].split('_');

export const catalogKeyFromFileName = (file: File): string | null =>
  nameParts(file.name).slice(1).join('_') || null;

export const customerNumberFromFileName = (file: File): string | null =>
  nameParts(file.name)[0] || null;

export const fromTabularFile: (TabularFile) => TabularOrder =
  tabularOrderFromTabularFile(tabularOrderSpecification);

export const tabularFile = (file: File): Promise<TabularFile> =>
  parsed(file, tabularFileSpecification);

export const tabularOrder = (file: File): Promise<TabularOrder> =>
  tabularFile(file)
    .then(fromTabularFile)
    .then((order) => ({
      ...order,
      customerNumber: order.customerNumber || customerNumberFromFileName(file),
      catalogKey: order.catalogKey || catalogKeyFromFileName(file),
    }));
